import { FitbitStoredRecord } from '../../../services/fitbitCollectionDataService';

export const structuredChartKinds: Record<string, 'limits' | 'duration' | 'grouped' | 'notifications'> = {
  'daily-heart-rate-zones': 'limits', 'activity-level': 'duration', 'time-in-heart-rate-zone': 'duration', 'sedentary-period': 'duration',
  'active-minutes': 'grouped', 'active-zone-minutes': 'grouped', 'calories-in-heart-rate-zone': 'grouped',
  'irregular-rhythm-notification': 'notifications',
};

export const measurementObject = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
export function finiteMeasurement(value: unknown): number | null {
  if (typeof value !== 'number' && (typeof value !== 'string' || !value.trim())) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}
export function zoneRanges(record: FitbitStoredRecord | undefined) {
  const zones = measurementObject(record?.measurement).heartRateZones;
  if (!Array.isArray(zones) || record?.valueState !== 'present') return [];
  return zones.flatMap(value => {
    const zone = measurementObject(value);
    const minimum = finiteMeasurement(zone.minBeatsPerMinute);
    const maximum = finiteMeasurement(zone.maxBeatsPerMinute);
    return typeof zone.heartRateZoneType === 'string' && minimum !== null && maximum !== null && minimum >= 0 && maximum >= minimum
      ? [{ zone: zone.heartRateZoneType, minimum, maximum }] : [];
  }).sort((a, b) => a.minimum - b.minimum);
}

export function groupedMeasurements(records: FitbitStoredRecord[], dataType: string, dates?: string[]) {
  const unit = dataType === 'calories-in-heart-rate-zone' ? 'kcal' : dataType === 'active-zone-minutes' ? 'credits' : 'min';
  const selectedDates = dates ? new Set(dates) : null;
  const groups = new Set<string>();
  const days = new Map<string, Map<string, number>>();
  for (const record of records) {
    if (record.dataType !== dataType || record.valueState !== 'present') continue;
    const measurement = measurementObject(record.measurement);
    const entries = dataType === 'active-minutes' ? measurement.activeMinutesRollupByActivityLevel :
      dataType === 'calories-in-heart-rate-zone' ? measurement.caloriesInHeartRateZones : [measurement];
    if (!Array.isArray(entries)) continue;
    for (const entry of entries) {
      const value = measurementObject(entry);
      const group = dataType === 'active-minutes' ? value.activityLevel : value.heartRateZone;
      const numeric = finiteMeasurement(dataType === 'active-minutes' ? value.activeMinutesSum :
        dataType === 'calories-in-heart-rate-zone' ? value.kcal : value.activeZoneMinutes);
      if (typeof group !== 'string' || !group || numeric === null || numeric < 0) continue;
      const timestamp = record.observedAt ? Date.parse(record.observedAt) : NaN;
      const date = dataType === 'active-zone-minutes'
        ? (Number.isFinite(timestamp) ? new Date(timestamp).toISOString().slice(0, 10) : '') : record.observedDate;
      if (!date || (selectedDates && !selectedDates.has(date))) continue;
      const totals = days.get(date) || new Map<string, number>();
      totals.set(group, (totals.get(group) || 0) + numeric);
      days.set(date, totals); groups.add(group);
    }
  }
  // Generated keys keep provider enum values out of Recharts' dotted-path lookup syntax.
  const series = Array.from(groups).sort().map((label, index) => ({ label, key: `group${index}` }));
  const points: Array<{ date: string; [key: string]: string | number | null }> = (dates || Array.from(days.keys()).sort()).map(date => ({
    date, ...Object.fromEntries(series.map(group => [group.key, days.get(date)?.get(group.label) ?? null])),
  }));
  return { unit, series, points };
}

export function notificationCounts(records: FitbitStoredRecord[], dates: string[]) {
  const counts = new Map<string, number>();
  const selectedDates = new Set(dates);
  let invalidTimes = 0;
  for (const record of records) {
    if (record.dataType !== 'irregular-rhythm-notification' || record.valueState !== 'present') continue;
    const start = record.observedAt ? Date.parse(record.observedAt) : NaN;
    if (!Number.isFinite(start)) { invalidTimes++; continue; }
    const date = new Date(start).toISOString().slice(0, 10);
    if (selectedDates.has(date)) counts.set(date, (counts.get(date) || 0) + 1);
  }
  return { invalidTimes, series: counts.size ? [{ key: 'group0', label: 'Notifications', color: '#a32138' }] : [],
    points: dates.map(date => ({ date, group0: counts.get(date) ?? null })) };
}

export function zoneHistory(records: FitbitStoredRecord[], dates: string[], zone: string) {
  const days = new Map<string, Array<{ minimum: number; maximum: number }>>();
  for (const record of records) {
    if (record.dataType !== 'daily-heart-rate-zones' || !record.observedDate) continue;
    const ranges = zoneRanges(record).filter(range => range.zone === zone);
    if (ranges.length) days.set(record.observedDate, [...(days.get(record.observedDate) || []), ...ranges]);
  }
  let conflictingDays = 0;
  const points = dates.map(date => {
    const ranges = days.get(date) || [];
    const conflict = ranges.some(range => range.minimum !== ranges[0].minimum || range.maximum !== ranges[0].maximum);
    if (conflict) conflictingDays++;
    return { date, limits: ranges.length && !conflict ? [ranges[0].minimum, ranges[0].maximum] : null };
  });
  return { points, conflictingDays };
}

export function intervalMinutes(record: FitbitStoredRecord) {
  if (!record.observedAt || !record.observedEndAt) return null;
  const duration = (Date.parse(record.observedEndAt) - Date.parse(record.observedAt)) / 60000;
  return Number.isFinite(duration) && duration >= 0 ? duration : null;
}
